import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiError } from '../../../core/auth/auth.models';
import { AuthService } from '../../../core/auth/auth.service';
import { ClientStatus } from '../../../core/client/client.models';
import { ClientService } from '../../../core/client/client.service';
import { STATUS_OPTIONS } from '../../../core/client/client.labels';
import { Organization } from '../../../core/organization/organization.models';
import { OrganizationService } from '../../../core/organization/organization.service';
import { PageHeader } from '../../../shared/page-header/page-header';

@Component({
  selector: 'app-client-form',
  imports: [ReactiveFormsModule, RouterLink, PageHeader],
  templateUrl: './client-form.html',
})
export class ClientForm implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(ClientService);
  private readonly organizationService = inject(OrganizationService);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly isEdit = signal(false);
  protected readonly saving = signal(false);
  protected readonly error = signal<string | null>(null);
  protected readonly statusOptions = STATUS_OPTIONS;
  protected readonly organizations = signal<Organization[]>([]);

  private id?: number;

  protected readonly form = this.fb.group({
    name: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    company: [''],
    organizationId: [null as number | null],
    notes: [''],
    status: ['ATIVO' as ClientStatus],
  });

  ngOnInit(): void {
    this.organizationService.list({ size: 1000 }).subscribe({ next: (res) => this.organizations.set(res.content) });
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.id = Number(idParam);
      this.isEdit.set(true);
      this.service.get(this.id).subscribe({ next: (detail) => this.form.patchValue(detail.client) });
    }
  }

  protected submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving.set(true);
    this.error.set(null);
    const v = this.form.getRawValue();
    const base = {
      name: v.name!,
      email: v.email!,
      phone: v.phone?.trim() ? v.phone : null,
      company: v.company?.trim() ? v.company : null,
      organizationId: v.organizationId ?? null,
      notes: v.notes?.trim() ? v.notes : null,
    };
    const request = this.isEdit()
      ? this.service.update(this.id!, { ...base, status: v.status as ClientStatus })
      : this.service.create(base);
    request.subscribe({
      next: (client) => this.router.navigate(['/', this.auth.currentTenant(), 'clientes', client.id]),
      error: (err: HttpErrorResponse) => {
        this.error.set((err.error as ApiError)?.message ?? 'Não foi possível salvar o cliente.');
        this.saving.set(false);
      },
    });
  }
}
